import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { exportCouponsCsv } from './adminExportUtils';
import AdminToggle from './AdminToggle';
import AdminSegmentToggle from './AdminSegmentToggle';
import {
  AdminPanelAlert,
  AdminPanelEmpty,
  AdminPanelHero,
  AdminPanelListCard,
  AdminPanelSection,
  AdminPanelTableWrap,
} from './AdminPanelUi';
import { useSaveButtonLabel } from './useSaveButtonLabel';

const emptyForm = () => ({
  id: 0,
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: '',
  max_discount: '',
  min_subtotal: '0',
  is_active: true,
  starts_at: '',
  expires_at: '',
  audience: 'all',
  restricted_user_id: '',
  restricted_email: '',
  require_login: false,
  first_order_only: false,
  max_uses_total: '',
  max_uses_per_user: '',
});

function formatDiscount(c) {
  if (c.discount_type === 'free_shipping') return 'Free shipping';
  if (c.discount_type === 'percent') {
    const cap = c.max_discount ? ` (max ₹${c.max_discount})` : '';
    return `${c.discount_value}%${cap}`;
  }
  return `₹${c.discount_value}`;
}

function formatRules(c) {
  const parts = [];
  if (c.restricted_user_id) parts.push('One customer');
  else if (c.restricted_email) parts.push(`Email: ${c.restricted_email}`);
  else parts.push('Everyone');
  if (c.require_login && !c.restricted_user_id && !c.restricted_email) parts.push('Login required');
  if (c.first_order_only) parts.push('First order');
  if (c.max_uses_total) parts.push(`Max ${c.max_uses_total} uses`);
  if (c.max_uses_per_user) parts.push(`${c.max_uses_per_user}/customer`);
  if (c.starts_at) parts.push(`From ${String(c.starts_at).slice(0, 10)}`);
  return parts.join(' · ');
}

export default function AdminCouponsPanel({ sessionToken, setStatus }) {
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [advanced, setAdvanced] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const saveBtn = useSaveButtonLabel(form.id ? 'Update coupon' : 'Add coupon');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [couponsRes, customersRes] = await Promise.all([
        fetch('/api/admin_coupons.php', { headers: adminAuthHeaders(sessionToken) }),
        fetch('/api/admin_coupons.php?action=customers', { headers: adminAuthHeaders(sessionToken) }),
      ]);
      const d = await couponsRes.json();
      const dc = await customersRes.json();
      if (!couponsRes.ok || !d.success) {
        setError(d.error || 'Could not load coupons.');
        setCoupons([]);
        return;
      }
      setCoupons(Array.isArray(d.coupons) ? d.coupons : []);
      setAdvanced(d.advanced !== false);
      if (customersRes.ok && dc.success) {
        setCustomers(Array.isArray(dc.customers) ? dc.customers : []);
      }
    } catch {
      setError('Could not load coupons.');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        id: c.id,
        label: [c.display_name || c.username, c.email].filter(Boolean).join(' — '),
      })),
    [customers]
  );

  const edit = (row) => {
    let audience = 'all';
    if (row.restricted_user_id) audience = 'customer';
    else if (row.restricted_email) audience = 'email';

    setForm({
      id: row.id,
      code: row.code,
      description: row.description || '',
      discount_type: row.discount_type || 'percent',
      discount_value: row.discount_type === 'free_shipping' ? '' : String(row.discount_value ?? ''),
      max_discount: row.max_discount != null ? String(row.max_discount) : '',
      min_subtotal: String(row.min_subtotal ?? 0),
      is_active: Boolean(row.is_active),
      starts_at: row.starts_at ? String(row.starts_at).slice(0, 10) : '',
      expires_at: row.expires_at ? String(row.expires_at).slice(0, 10) : '',
      audience,
      restricted_user_id: row.restricted_user_id ? String(row.restricted_user_id) : '',
      restricted_email: row.restricted_email || '',
      require_login: Boolean(row.require_login),
      first_order_only: Boolean(row.first_order_only),
      max_uses_total: row.max_uses_total != null ? String(row.max_uses_total) : '',
      max_uses_per_user: row.max_uses_per_user != null ? String(row.max_uses_per_user) : '',
    });
  };

  const buildPayload = () => {
    const audience = form.audience;
    return {
      id: form.id || undefined,
      code: form.code.trim(),
      description: form.description.trim(),
      discount_type: form.discount_type,
      discount_value:
        form.discount_type === 'free_shipping' ? 0 : Number(form.discount_value),
      max_discount:
        form.discount_type === 'percent' && form.max_discount.trim()
          ? Number(form.max_discount)
          : null,
      min_subtotal: Number(form.min_subtotal) || 0,
      is_active: form.is_active,
      starts_at: form.starts_at.trim() || null,
      expires_at: form.expires_at.trim() || null,
      restricted_user_id:
        audience === 'customer' && form.restricted_user_id
          ? Number(form.restricted_user_id)
          : null,
      restricted_email: audience === 'email' ? form.restricted_email.trim() : '',
      require_login: form.require_login || audience === 'customer' || audience === 'email',
      first_order_only: form.first_order_only,
      max_uses_total: form.max_uses_total.trim() ? Number(form.max_uses_total) : null,
      max_uses_per_user: form.max_uses_per_user.trim() ? Number(form.max_uses_per_user) : null,
    };
  };

  const save = async (e) => {
    e.preventDefault();
    saveBtn.startSaving();
    setError('');
    try {
      const r = await fetch('/api/admin_coupons.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify(buildPayload()),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not save coupon.');
        saveBtn.markFailed();
        return;
      }
      setStatus(form.id ? 'Coupon updated.' : 'Coupon created.');
      setForm(emptyForm());
      await load();
      saveBtn.markSaved();
    } catch {
      setError('Could not save coupon.');
      saveBtn.markFailed();
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    setError('');
    try {
      const r = await fetch('/api/admin_coupons.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ action: 'delete', id }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not delete.');
        return;
      }
      setStatus('Coupon deleted.');
      if (form.id === id) setForm(emptyForm());
      await load();
    } catch {
      setError('Could not delete.');
    }
  };

  const showValue = form.discount_type !== 'free_shipping';

  return (
    <div className="admin-coupons admin-panel-workspace-root">
      <AdminPanelHero
        title="Discount coupons"
        lead="Promo codes for everyone, or private codes for one customer. Limits, schedules, and free-shipping offers — enforced at checkout and when the order is placed."
      />

      {!advanced ? (
        <AdminPanelAlert variant="warning">
          Run <code>database/migrate_catalog_all.sql</code> on your database to unlock customer-specific
          coupons, usage limits, start dates, and free shipping.
        </AdminPanelAlert>
      ) : null}

      {error ? <AdminPanelAlert variant="danger">{error}</AdminPanelAlert> : null}

      <AdminPanelSection
        title={form.id ? 'Edit coupon' : 'New coupon'}
        subtitle="Codes are not case-sensitive. One coupon per order."
      >
        <form onSubmit={save} className="admin-coupons-form-grid">
          <div className="admin-coupons-form-grid__code">
            <label className="form-label small mb-1" htmlFor="coupon-code">
              Code
            </label>
            <input
              id="coupon-code"
              className="form-control text-uppercase"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              required
            />
          </div>
          <div className="admin-coupons-form-grid__desc">
            <label className="form-label small mb-1" htmlFor="coupon-desc">
              Internal note (shown to customer when applied)
            </label>
            <input
              id="coupon-desc"
              className="form-control"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Sorry for the delay — 15% off"
            />
          </div>
          <div className="admin-coupons-form-grid__type">
            <span className="form-label small mb-1 d-block">Benefit</span>
            <AdminSegmentToggle
              ariaLabel="Benefit type"
              value={form.discount_type}
              onChange={(v) => setForm((f) => ({ ...f, discount_type: v }))}
              options={[
                { value: 'percent', label: '% off' },
                { value: 'fixed', label: '₹ off' },
                ...(advanced ? [{ value: 'free_shipping', label: 'Free ship' }] : []),
              ]}
            />
          </div>
          {showValue ? (
            <div className="admin-coupons-form-grid__val">
              <label className="form-label small mb-1" htmlFor="coupon-val">
                {form.discount_type === 'percent' ? 'Percent off' : 'Rupees off'}
              </label>
              <input
                id="coupon-val"
                type="text"
                inputMode="decimal"
                className="form-control"
                value={form.discount_value}
                onChange={(e) =>
                  setForm((f) => ({ ...f, discount_value: e.target.value.replace(/[^\d.]/g, '') }))
                }
                required
              />
            </div>
          ) : null}
          {form.discount_type === 'percent' && advanced ? (
            <div className="admin-coupons-form-grid__maxd">
              <label className="form-label small mb-1" htmlFor="coupon-maxd">
                Max discount ₹ (optional)
              </label>
              <input
                id="coupon-maxd"
                type="text"
                inputMode="decimal"
                className="form-control"
                value={form.max_discount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, max_discount: e.target.value.replace(/[^\d.]/g, '') }))
                }
                placeholder="Cap for % coupons"
              />
            </div>
          ) : null}
          <div className="admin-coupons-form-grid__min">
            <label className="form-label small mb-1" htmlFor="coupon-min">
              Min order ₹
            </label>
            <input
              id="coupon-min"
              type="text"
              inputMode="numeric"
              className="form-control"
              value={form.min_subtotal}
              onChange={(e) =>
                setForm((f) => ({ ...f, min_subtotal: e.target.value.replace(/[^\d.]/g, '') }))
              }
            />
          </div>
          <div className="admin-coupons-form-grid__start">
            <label className="form-label small mb-1" htmlFor="coupon-start">
              Starts (optional)
            </label>
            <input
              id="coupon-start"
              type="date"
              className="form-control"
              value={form.starts_at}
              onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
              disabled={!advanced}
            />
          </div>
          <div className="admin-coupons-form-grid__exp">
            <label className="form-label small mb-1" htmlFor="coupon-exp">
              Expires (optional)
            </label>
            <input
              id="coupon-exp"
              type="date"
              className="form-control"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
            />
          </div>

          {advanced ? (
            <>
              <div className="admin-coupons-form-grid__audience">
                <span className="form-label small mb-1 d-block">Who can use it</span>
                <AdminSegmentToggle
                  ariaLabel="Audience"
                  value={form.audience}
                  onChange={(v) => setForm((f) => ({ ...f, audience: v }))}
                  options={[
                    { value: 'all', label: 'Everyone' },
                    { value: 'customer', label: 'One customer' },
                    { value: 'email', label: 'One email' },
                  ]}
                />
              </div>
              {form.audience === 'customer' ? (
                <div className="admin-coupons-form-grid__customer">
                  <label className="form-label small mb-1" htmlFor="coupon-customer">
                    Customer account
                  </label>
                  <select
                    id="coupon-customer"
                    className="form-select"
                    value={form.restricted_user_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, restricted_user_id: e.target.value, require_login: true }))
                    }
                    required
                  >
                    <option value="">Select customer…</option>
                    {customerOptions.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {form.audience === 'email' ? (
                <div className="admin-coupons-form-grid__email">
                  <label className="form-label small mb-1" htmlFor="coupon-email">
                    Allowed email
                  </label>
                  <input
                    id="coupon-email"
                    type="email"
                    className="form-control"
                    value={form.restricted_email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, restricted_email: e.target.value, require_login: true }))
                    }
                    required
                  />
                </div>
              ) : null}
              <div className="admin-coupons-form-grid__limits">
                <label className="form-label small mb-1" htmlFor="coupon-max-total">
                  Total uses (blank = unlimited)
                </label>
                <input
                  id="coupon-max-total"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  value={form.max_uses_total}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, max_uses_total: e.target.value.replace(/\D/g, '') }))
                  }
                />
              </div>
              <div className="admin-coupons-form-grid__limits-user">
                <label className="form-label small mb-1" htmlFor="coupon-max-user">
                  Uses per customer
                </label>
                <input
                  id="coupon-max-user"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  value={form.max_uses_per_user}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, max_uses_per_user: e.target.value.replace(/\D/g, '') }))
                  }
                  placeholder="e.g. 1 for one-time"
                />
              </div>
              <div className="admin-coupons-form-grid__flags d-flex flex-wrap gap-3 align-items-center">
                <AdminToggle
                  id="coupon-first-order"
                  label="First order only"
                  checked={form.first_order_only}
                  onChange={(v) => setForm((f) => ({ ...f, first_order_only: v }))}
                />
                {form.audience === 'all' ? (
                  <AdminToggle
                    id="coupon-login"
                    label="Must be signed in"
                    checked={form.require_login}
                    onChange={(v) => setForm((f) => ({ ...f, require_login: v }))}
                  />
                ) : null}
              </div>
            </>
          ) : null}

          <div className="admin-coupons-form-grid__actions d-flex flex-wrap gap-2 align-items-center">
            <AdminToggle
              id="coupon-active"
              label="Active"
              checked={form.is_active}
              onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
            />
            <button
              type="submit"
              className={`btn btn-sm${saveBtn.isSaved ? ' btn-success' : ' btn-primary'}`}
              disabled={saveBtn.isSaving}
            >
              {saveBtn.label}
            </button>
            {form.id ? (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm(emptyForm())}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </AdminPanelSection>

      <AdminPanelListCard
        kicker="All coupons"
        title="Saved codes"
        count={coupons.length}
        actions={
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={loading || !coupons.length}
            onClick={() => exportCouponsCsv(coupons)}
          >
            Export CSV
          </button>
        }
      >
        {loading ? (
          <AdminPanelEmpty message="Loading coupons…" />
        ) : coupons.length === 0 ? (
          <AdminPanelEmpty message="No coupons yet. Add one above." />
        ) : (
          <AdminPanelTableWrap>
            <table className="table table-sm mb-0 align-middle">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Benefit</th>
                  <th>Audience</th>
                  <th>Uses</th>
                  <th>Schedule</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong className="text-uppercase">{c.code}</strong>
                      {c.description ? <div className="small text-muted">{c.description}</div> : null}
                    </td>
                    <td className="small">
                      {formatDiscount(c)}
                      {Number(c.min_subtotal) > 0 ? (
                        <div className="text-muted">Min ₹{Number(c.min_subtotal).toLocaleString('en-IN')}</div>
                      ) : null}
                    </td>
                    <td className="small text-muted">{formatRules(c)}</td>
                    <td className="small">
                      {c.usage_count ?? 0}
                      {c.max_uses_total ? ` / ${c.max_uses_total}` : ''}
                    </td>
                    <td className="small">
                      {c.starts_at ? <div>From {String(c.starts_at).slice(0, 10)}</div> : null}
                      {c.expires_at ? <div>Until {String(c.expires_at).slice(0, 10)}</div> : '—'}
                    </td>
                    <td>
                      <span className={`badge ${c.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {c.is_active ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => edit(c)}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => remove(c.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminPanelTableWrap>
        )}
      </AdminPanelListCard>
    </div>
  );
}
